import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from "react";
import { createUnassignedFile, getUnassignedFiles, deleteUnassignedFile, uploadFile } from "@/network/network";
import { toast } from "sonner";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EntityRecord } from "@/types/entity";
import { getFileExtension } from "@/modern/utils";

export interface UnassignedFile {
  id: number;
  document_id: number;
  url: string;
  thumbnail_url: string;
  creator: number;
  name: string; // File name extracted from URL or provided by API
  created_at: string;
  size: number;
}

export type FileType = "all" | "images" | "videos" | "documents" | "audio";
export type SortOption = "name" | "date" | "type" | "size";
export type ViewMode = "grid" | "list";

interface MyMediaContextValue {
  files: UnassignedFile[];
  totalFilesCount: number;
  selectedFiles: Set<number>;
  isLoading: boolean;
  isAssigning: boolean;
  searchQuery: string;
  fileTypeFilter: FileType;
  sortOption: SortOption;
  viewMode: ViewMode;
  setSearchQuery: (query: string) => void;
  setFileTypeFilter: (type: FileType) => void;
  setSortOption: (option: SortOption) => void;
  setViewMode: (mode: ViewMode) => void;
  toggleFileSelection: (fileId: number) => void;
  selectAllFiles: () => void;
  clearSelection: () => void;
  fetchUnassignedFiles: () => Promise<void>;
  uploadAndCreateUnassignedFile: (file: File) => Promise<void>;
  deleteUnassignedFileById: (id: number) => Promise<void>;
  assignFilesToEntity: (fileIds: number[], entityRecord: EntityRecord) => Promise<void>;
  entityRecord: EntityRecord | null;
  setEntityRecord: (record: EntityRecord | null) => void;
  fileTypeCounts: Record<FileType, number>;
}

const MyMediaContext = createContext<MyMediaContextValue | undefined>(undefined);

export const useMyMediaContext = () => {
  const context = useContext(MyMediaContext);
  if (!context) {
    throw new Error("useMyMediaContext must be used within MyMediaProvider");
  }
  return context;
};

interface MyMediaProviderProps {
  children: ReactNode;
  initialEntityRecord?: EntityRecord | null;
}

export const MyMediaProvider = ({ children, initialEntityRecord = null }: MyMediaProviderProps) => {
  const { project } = useSwivuBryntumContext();
  const [files, setFiles] = useState<UnassignedFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState<FileType>("all");
  const [sortOption, setSortOption] = useState<SortOption>("name");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [entityRecord, setEntityRecord] = useState<EntityRecord | null>(initialEntityRecord);

  // Helper to extract file name from URL
  const extractFileName = useCallback((url: string, id: number): string => {
    try {
      const urlParts = url.split("/");
      const fileName = urlParts[urlParts.length - 1];
      // Remove query parameters if any
      const cleanName = fileName.split("?")[0];
      // If it looks like a valid filename with extension, use it
      if (cleanName.includes(".") && cleanName.length > 5) {
        return decodeURIComponent(cleanName);
      }
    } catch (e) {
      // Fallback
    }
    return `file-${id}`;
  }, []);

  // Helper to get file type category
  const getFileTypeCategory = useCallback((file: UnassignedFile): FileType => {
    const fileName = file.name || extractFileName(file.url, file.id);
    const ext = getFileExtension(fileName).toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "ico"].includes(ext)) {
      return "images";
    }
    if (["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v"].includes(ext)) {
      return "videos";
    }
    if (["mp3", "wav", "ogg", "m4a", "flac", "aac", "wma"].includes(ext)) {
      return "audio";
    }
    // Archives and other files are categorized as documents
    return "documents";
  }, [extractFileName]);

  // Clear selection when entity becomes unavailable
  useEffect(() => {
    if (!entityRecord) {
      setSelectedFiles(new Set());
    }
  }, [entityRecord]);

  const fetchUnassignedFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await getUnassignedFiles();
      // Add name property to files if missing
      const filesWithNames = (data || []).map((file: UnassignedFile) => ({
        ...file,
        name: file.name || extractFileName(file.url, file.id),
      }));
      setFiles(filesWithNames);
    } catch (error) {
      console.error("Failed to fetch unassigned files:", error);
      toast.error("Failed to load files. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [extractFileName]);

  const uploadAndCreateUnassignedFile = useCallback(async (file: File) => {
    try {
      // First upload the file to get document_id
      const { data: uploadData } = await uploadFile(file, file.name);
      const documentId = uploadData.id;

      // Then create unassigned file record
      await createUnassignedFile(documentId);

      // Refresh the list (this will add the name property from the file)
      await fetchUnassignedFiles();
    } catch (error) {
      console.error("Failed to upload file:", error);
      throw error;
    }
  }, [fetchUnassignedFiles]);

  const deleteUnassignedFileById = useCallback(async (id: number) => {
    try {
      await deleteUnassignedFile(id);
      setFiles((prev) => prev.filter((f) => f.id !== id));
      setSelectedFiles((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success("File deleted successfully");
    } catch (error) {
      console.error("Failed to delete file:", error);
      toast.error("Failed to delete file. Please try again.");
    }
  }, []);

  const assignFilesToEntity = useCallback(
    async (fileIds: number[], entityRecord: EntityRecord) => {
      if (!entityRecord || !project) {
        toast.error("No entity selected for assignment");
        return;
      }

      setIsAssigning(true);
      try {
        // Get document_ids from selected files
        const selectedFileRecords = files.filter((f) => fileIds.includes(f.id));
        const documentIds = selectedFileRecords.map((f) => f.document_id);

        // Create records for boardFilesStore
        const records = documentIds.map((documentId) => ({
          documentId,
          [entityRecord.entityType]: entityRecord.id,
        }));

        // Add to boardFilesStore (this will automatically remove from unassigned list via backend)
        project.boardFilesStore.add(records);

        await project.sync();

        // Refresh unassigned files list
        await fetchUnassignedFiles();

        // Clear selection
        setSelectedFiles(new Set());

        const fileCount = fileIds.length;
        const fileText = fileCount === 1 ? "file" : "files";
        toast.success(`Successfully assigned ${fileCount} ${fileText} to ${entityRecord.name || "entity"}`);
      } catch (error) {
        console.error("Failed to assign files:", error);
        toast.error("Failed to assign files. Please try again.");
      } finally {
        setIsAssigning(false);
      }
    },
    [files, project, fetchUnassignedFiles]
  );

  const toggleFileSelection = useCallback((fileId: number) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  // Get file type counts
  const fileTypeCounts = useMemo(() => {
    const counts: Record<FileType, number> = {
      all: files.length,
      images: 0,
      videos: 0,
      documents: 0,
      audio: 0,
    };

    files.forEach((file) => {
      const category = getFileTypeCategory(file);
      if (category !== "all") {
        counts[category]++;
      }
    });

    return counts;
  }, [files, getFileTypeCategory]);

  // Filter and sort files
  const filteredFiles = useMemo(() => {
    let result = [...files];

    // Apply file type filter
    if (fileTypeFilter !== "all") {
      result = result.filter((file) => getFileTypeCategory(file) === fileTypeFilter);
    }

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((file) => {
        const fileName = file.name || extractFileName(file.url, file.id);
        return fileName.toLowerCase().includes(query);
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      const nameA = (a.name || extractFileName(a.url, a.id)).toLowerCase();
      const nameB = (b.name || extractFileName(b.url, b.id)).toLowerCase();
      const typeA = getFileTypeCategory(a);
      const typeB = getFileTypeCategory(b);
      const extA = getFileExtension(a.name || extractFileName(a.url, a.id));
      const extB = getFileExtension(b.name || extractFileName(b.url, b.id));

      switch (sortOption) {
        case "name":
          return nameA.localeCompare(nameB);
        case "type":
          if (typeA !== typeB) {
            return typeA.localeCompare(typeB);
          }
          return extA.localeCompare(extB);
        case "date": {
          // Sort by created_at (newest first)
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        }
        case "size": {
          // Sort by size (largest first)
          const sizeA = a.size || 0;
          const sizeB = b.size || 0;
          return sizeB - sizeA;
        }
        default:
          return 0;
      }
    });

    return result;
  }, [files, fileTypeFilter, searchQuery, sortOption, getFileTypeCategory, extractFileName]);

  const selectAllFiles = useCallback(() => {
    setSelectedFiles(new Set(filteredFiles.map((f) => f.id)));
  }, [filteredFiles]);

  const clearSelection = useCallback(() => {
    setSelectedFiles(new Set());
  }, []);

  // Load files on mount
  useEffect(() => {
    fetchUnassignedFiles();
  }, [fetchUnassignedFiles]);

  return (
    <MyMediaContext.Provider
      value={{
        files: filteredFiles,
        totalFilesCount: files.length,
        selectedFiles,
        isLoading,
        isAssigning,
        searchQuery,
        fileTypeFilter,
        sortOption,
        viewMode,
        setSearchQuery,
        setFileTypeFilter,
        setSortOption,
        setViewMode,
        toggleFileSelection,
        selectAllFiles,
        clearSelection,
        fetchUnassignedFiles,
        uploadAndCreateUnassignedFile,
        deleteUnassignedFileById,
        assignFilesToEntity,
        entityRecord,
        setEntityRecord,
        fileTypeCounts,
      }}
    >
      {children}
    </MyMediaContext.Provider>
  );
};
